import React, { useEffect, useMemo, useState } from "react";
import { Cake, CalendarDays, Search, Copy, User, ChevronLeft, ChevronRight, Eye, AlertTriangle, Heart, MessageCircle } from "lucide-react";
import { waNomor } from "../components/WaButtons";
import { api, fileUrl } from "../lib/api";
import { friendlyErr } from "../lib/friendlyErr";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Avatar, AvatarImage, AvatarFallback } from "../components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import StatusBadge from "../components/StatusBadge";
import { UlangTahunPernikahan } from "../components/UlangTahunPernikahan";
import { UcapanGambar } from "../components/UcapanGambar";
import { toast } from "sonner";

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const BULAN = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
const KATEGORI = ["Umum", "Anak", "Remaja", "Dewasa", "Lansia"];
const MODES = [["hari_ini", "Hari Ini"], ["besok", "Besok"], ["minggu_ini", "Minggu Ini"], ["bulan_ini", "Bulan Ini"], ["tanggal", "Pilih Tanggal"], ["bulan", "Pilih Bulan"]];
const PRIMARY = "bg-[hsl(224,64%,33%)] hover:bg-[hsl(224,64%,28%)]";

export const salinTeks = async (text) => {
  try {
    if (navigator.clipboard?.writeText) { await navigator.clipboard.writeText(text); return true; }
  } catch (_) { /* fallback */ }
  const ta = document.createElement("textarea");
  ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
  document.body.appendChild(ta); ta.select();
  const ok = document.execCommand("copy");
  ta.remove();
  return ok;
};

const StatCard = ({ icon: Icon, label, value, testId }) => (
  <Card className="p-4 rounded-2xl border-slate-200/80 flex items-center gap-3">
    <div className="h-11 w-11 rounded-xl bg-[hsl(224,64%,33%)]/10 text-[hsl(224,64%,33%)] flex items-center justify-center shrink-0"><Icon className="h-5 w-5" /></div>
    <div className="min-w-0"><div className="text-[11px] uppercase tracking-wider text-slate-500 truncate">{label}</div><div className="text-2xl font-bold text-slate-900" data-testid={testId}>{value}</div></div>
  </Card>
);

const JemaatCard = ({ j, onPreview, onCopy }) => (
  <Card className="p-4 rounded-2xl border-slate-200/80 flex flex-col gap-3 ulang-tahun-card" data-testid={`ulang-tahun-card-${j.id}`}>
    <div className="flex items-start gap-3 min-w-0">
      <Avatar className="h-14 w-14 rounded-xl shrink-0">
        {j.foto_path && <AvatarImage src={fileUrl(j.foto_path)} className="object-cover" />}
        <AvatarFallback className="rounded-xl bg-slate-100 text-slate-400"><User className="h-6 w-6" /></AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <div className="font-semibold text-slate-900 break-words" data-testid={`ulang-tahun-nama-${j.id}`}>{j.sapaan ? `${j.sapaan} ` : ""}{j.nama_lengkap}</div>
        {j.perlu_dilengkapi && <Badge variant="outline" className="mt-1 bg-amber-50 text-amber-800 border-amber-200 text-[11px]" data-testid={`perlu-dilengkapi-${j.id}`}>Sapaan: Perlu dilengkapi</Badge>}
        {j.nama_panggilan && <div className="text-xs text-slate-500">"{j.nama_panggilan}"</div>}
        <div className="text-xs text-slate-500 font-mono mt-0.5">{j.nij}</div>
        <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-slate-700">
          <span className="inline-flex items-center gap-1"><Cake className="h-4 w-4 text-[hsl(24,94%,40%)]" /> {j.ulang_tahun}</span>
          {j.usia != null && <Badge variant="secondary" data-testid={`ulang-tahun-usia-${j.id}`}>Usia {j.usia} tahun</Badge>}
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <StatusBadge value={j.status_jemaat} />
        </div>
        {j.sapaan_warning && <div className="mt-2 text-xs text-amber-700 inline-flex items-start gap-1" data-testid={`sapaan-warning-${j.id}`}><AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" /> {j.sapaan_warning}</div>}
      </div>
    </div>
    <div className="flex gap-2">
      <Button variant="outline" className="flex-1 min-h-[44px] rounded-xl" onClick={() => onPreview(j)} data-testid={`preview-ucapan-btn-${j.id}`}><Eye className="h-4 w-4 mr-2" /> Preview</Button>
      <Button className={`flex-1 min-h-[44px] rounded-xl ${PRIMARY}`} onClick={() => onCopy(j)} data-testid={`salin-ucapan-btn-${j.id}`}><Copy className="h-4 w-4 mr-2" /> Salin Ucapan</Button>
      {waNomor(j)
        ? <Button asChild variant="outline" className="min-h-[44px] rounded-xl border-emerald-300 text-emerald-700 hover:bg-emerald-50"><a href={`https://wa.me/${waNomor(j)}?text=${encodeURIComponent(j.ucapan)}`} target="_blank" rel="noreferrer" title="Kirim ucapan via WhatsApp" data-testid={`wa-ucapan-btn-${j.id}`}><MessageCircle className="h-4 w-4 mr-1" /> WA</a></Button>
        : <span className="text-xs text-slate-400 inline-flex items-center px-1 min-h-[44px] whitespace-nowrap" data-testid={`wa-ucapan-none-${j.id}`}>tanpa no. WA</span>}
    </div>
  </Card>
);

export default function UlangTahunPage() {
  const today = useMemo(() => new Date(), []);
  const [mode, setMode] = useState("hari_ini");
  const [tanggal, setTanggal] = useState(iso(today));
  const [bulan, setBulan] = useState(today.getMonth() + 1);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, page: 1, total_pages: 1, label: "" });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [kategori, setKategori] = useState("Umum");

  useEffect(() => { const t = setTimeout(() => { setQuery(search.trim()); setPage(1); }, 350); return () => clearTimeout(t); }, [search]);
  useEffect(() => { api.get("/ulang-tahun/stats", { params: { tanggal: iso(today) } }).then((r) => setStats(r.data)).catch(() => {}); }, [today]);

  const params = useMemo(() => {
    const p = { page, page_size: 20 };
    if (query) p.search = query;
    if (mode === "hari_ini") p.tanggal = iso(today);
    else if (mode === "besok") p.tanggal = iso(addDays(today, 1));
    else if (mode === "minggu_ini") { const dow = (today.getDay() + 6) % 7; const mon = addDays(today, -dow); p.dari = iso(mon); p.sampai = iso(addDays(mon, 6)); }
    else if (mode === "bulan_ini") p.bulan = today.getMonth() + 1;
    else if (mode === "tanggal") p.tanggal = tanggal;
    else p.bulan = bulan;
    return p;
  }, [mode, tanggal, bulan, query, page, today]);

  useEffect(() => {
    setLoading(true);
    api.get("/ulang-tahun", { params }).then((r) => setData(r.data)).catch((e) => toast.error(friendlyErr(e))).finally(() => setLoading(false));
  }, [params]);

  const ucapanOf = (j, k = "Umum") => j.ucapan_per_kategori?.[k] || j.ucapan;
  const openPreview = (j) => { setKategori("Umum"); setPreview(j); };
  const copy = async (j, k = "Umum") => {
    const ok = await salinTeks(ucapanOf(j, k));
    if (ok) toast.success("Ucapan berhasil disalin."); else toast.error("Gagal menyalin ucapan. Silakan salin manual dari Preview.");
  };
  const emptyMsg = mode === "hari_ini" ? "Belum ada jemaat yang berulang tahun hari ini." : "Belum ada jemaat yang berulang tahun.";

  return (
    <div className="space-y-6 ulang-tahun-module" data-testid="ulang-tahun-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Ulang Tahun Jemaat</h1>
        <p className="text-sm text-slate-500 mt-1">Daftar jemaat yang berulang tahun berdasarkan tanggal lahir.</p>
        {stats && <p className="text-sm font-medium text-slate-700 mt-1" data-testid="tanggal-hari-ini">{stats.label}</p>}
      </div>

      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3" data-testid="ulang-tahun-stats">
          <StatCard icon={Cake} label="Ulang Tahun Hari Ini" value={stats.hari_ini} testId="stat-hari-ini" />
          <StatCard icon={CalendarDays} label="Besok" value={stats.besok} testId="stat-besok" />
          <StatCard icon={CalendarDays} label="Bulan Ini" value={stats.bulan_ini} testId="stat-bulan-ini" />
          <StatCard icon={Heart} label="Pernikahan Hari Ini" value={stats.pernikahan_hari_ini ?? 0} testId="stat-pernikahan-hari-ini" />
        </div>
      )}

      <Card className="p-4 rounded-2xl border-slate-200/80 space-y-3">
        <div className="flex flex-wrap gap-2" data-testid="filter-mode">
          {MODES.map(([m, l]) => (
            <Button key={m} size="sm" variant={mode === m ? "default" : "outline"} className={`rounded-lg min-h-[40px] ${mode === m ? PRIMARY : ""}`} onClick={() => { setMode(m); setPage(1); }} data-testid={`mode-${m}-btn`}>{l}</Button>
          ))}
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          {mode === "tanggal" && <Input type="date" value={tanggal} onChange={(e) => { setTanggal(e.target.value); setPage(1); }} className="min-h-[44px] rounded-xl sm:max-w-[220px]" data-testid="pilih-tanggal-input" />}
          {mode === "bulan" && (
            <select value={bulan} onChange={(e) => { setBulan(Number(e.target.value)); setPage(1); }} className="min-h-[44px] rounded-xl border border-input bg-background px-3 text-sm sm:max-w-[220px]" data-testid="pilih-bulan-select">
              {BULAN.map((b, i) => <option key={b} value={i + 1}>{b}</option>)}
            </select>
          )}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input placeholder="Cari nama atau NIJ..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 min-h-[44px] rounded-xl" data-testid="search-ulang-tahun-input" />
          </div>
        </div>
        <div className="text-sm text-slate-600" data-testid="periode-label">{data.label && <>Periode: <span className="font-medium">{data.label}</span> — <span className="font-semibold" data-testid="total-ulang-tahun">{data.total}</span> jemaat</>}</div>
      </Card>

      {loading ? (
        <div className="text-center py-16 text-slate-400" data-testid="loading">Memuat data...</div>
      ) : data.items.length === 0 ? (
        <Card className="p-12 text-center rounded-2xl" data-testid="empty-state">
          <Cake className="h-12 w-12 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500 font-medium">{emptyMsg}</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="ulang-tahun-grid">
          {data.items.map((j) => <JemaatCard key={j.id} j={j} onPreview={openPreview} onCopy={(x) => copy(x, "Umum")} />)}
        </div>
      )}

      {data.total_pages > 1 && (
        <div className="flex items-center justify-center gap-3">
          <Button variant="outline" size="icon" className="rounded-xl" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="prev-page-btn"><ChevronLeft className="h-4 w-4" /></Button>
          <span className="text-sm text-slate-600">Hal {data.page} / {data.total_pages}</span>
          <Button variant="outline" size="icon" className="rounded-xl" disabled={page >= data.total_pages} onClick={() => setPage((p) => p + 1)} data-testid="next-page-btn"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      )}

      <UlangTahunPernikahan params={params} salin={salinTeks} />

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-lg w-[calc(100vw-2rem)] rounded-2xl" data-testid="preview-ucapan-dialog">
          <DialogHeader>
            <DialogTitle>Preview Ucapan</DialogTitle>
            <DialogDescription>Tinjau ucapan sebelum menyalin. Nama dan sapaan diambil otomatis dari Data Jemaat.</DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 text-sm">
            <label htmlFor="kategori-ucapan" className="text-slate-600 shrink-0">Template:</label>
            <select id="kategori-ucapan" value={kategori} onChange={(e) => setKategori(e.target.value)} className="min-h-[40px] flex-1 rounded-xl border border-input bg-background px-3 text-sm" data-testid="kategori-ucapan-select">
              {KATEGORI.map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
          </div>
          {preview && <>
            <UcapanGambar meta={data.template_meta?.[kategori]} posisi="atas" />
            <pre className="whitespace-pre-wrap font-sans text-sm text-slate-800 bg-slate-50 rounded-xl p-4 border border-slate-100 max-h-[45vh] overflow-y-auto" data-testid="preview-ucapan-text">{ucapanOf(preview, kategori)}</pre>
            <UcapanGambar meta={data.template_meta?.[kategori]} posisi="bawah" />
          </>}
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" className="min-h-[44px] rounded-xl" onClick={() => setPreview(null)} data-testid="preview-tutup-btn">Tutup</Button>
            <Button className={`min-h-[44px] rounded-xl ${PRIMARY}`} onClick={() => preview && copy(preview, kategori)} data-testid="preview-salin-btn"><Copy className="h-4 w-4 mr-2" /> Salin Ucapan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
